/**
 * RowSelect is a BatchGeoTable plugin that let's users select rows in various
 * ways and gives developers event hooks into these actions. When you add
 * RowSelect to your BatchGeoTable users will be able to:
 *
 * 1. Click on row to select it
 * 2. cmd/ctrl+click rows to select or deselect multiple rows
 * 3. Shift+click rows to select a range of rows.
 * 4. Click and drag on rows to select a range with your mouse
 *
 * For developers, you can use the public API to listen for `rowselect` and
 * `rowdeselect` events. Both events return the current collection of selected
 * rows. Example usage:
 *
 *  batchGeoTable.addListener('rowselect', function (rows) {
 *    console.log('SELECT: ', rows)
 *  });
 */
(function () {
  var RowSelect = function () {
    var self = this;
    var tapped = false; //check for double tap
    var isMouseDown = false,
      lastSelectedElement,
      $tbody = $(self.container).find("tbody");

    /**
     * Returns if the ctrl OR cmd key was being pressed down in an event so that
     * Windows and Mac have the same behavior.
     *
     * @param  {object}  event A DOM event object
     * @return {Boolean}       Returns true if cmd or ctrl was being pressed
     */
    var isCtrlKey = function (event) {
      return event.metaKey || event.ctrlKey;
    };

    /**
     * Clears a user text selection (so that we can do things like drag to
     * select rows without text being selected)
     *
     * @return {undefined} Returns nothing... just darkness
     */
    var clearTextSelection = function () {
      if (window.getSelection) {
        if (window.getSelection().empty) {
          // Chrome
          window.getSelection().empty();
        } else if (window.getSelection().removeAllRanges) {
          // Firefox
          window.getSelection().removeAllRanges();
        }
      } else if (document.selection) {
        // IE
        document.selection.empty();
      }
    };

    /**
     * Returns the currently selected row IDs
     */
    var getSelectedRowIds = function () {
      return BatchGeoStore.getState().RowSelect.selection;
    };

    /**
     * Simply returns a boolean if the row is selected or not
     */
    var isSelected = function (id) {
      return getSelectedRowIds().indexOf(id) > -1;
    };

    /**
     * Selects the row by the ID or array of IDs. Selecting means pushing the
     * ID into the collection of selected IDs, adding a selected class and
     * emitting a selected event for developers to hook into the event. When
     * passing an array of IDs it will only fire 1 select event per
     * selectRow() call. So, passing either [1,2,3,4] or 1 only fires the
     * select once.
     *
     * You can pass an optional second param, `options`, which send the options
     * from the select() call to the `rowselect` listeners. You can do this so
     * you can have different behaviors based on different types of row selects
     * in your app.
     */
    var selectRow = function (ids, options) {
      BatchGeoStore.dispatch({
        type: "ROW_SELECT_SELECT",
        // Convert all passed IDs to arrays to make it easier to handle
        selectedRows: !(ids instanceof Array) ? [ids] : ids,
        options: options,
      });
    };

    /**
     * Just like selectRow but adds to an existing selection. Read comments for
     * selectRow above's docs.
     */
    var selectAddRow = function (ids, options) {
      BatchGeoStore.dispatch({
        type: "ROW_SELECT_SELECTADD",
        // Convert all passed IDs to arrays to make it easier to handle
        selectedRows: !(ids instanceof Array) ? [ids] : ids,
        options: options,
      });
    };

    /**
     * Selects all possible rows based on the visible rows to the user
     * @return {undefined}
     */
    var selectAllRows = function () {
      BatchGeoStore.dispatch({
        type: "ROW_SELECT_SELECT",
        selectedRows: self.getDisplayData().map(function (row) {
          return row.id;
        }),
      });
    };

    /**
     * Deselects the row by the ID. Deselecting means remove the item from the
     * collection, remove the selected class on the row, and emit a deselect
     * event for developers to hook into
     */
    var deselectRow = function (id) {
      const openInfoWindow =
        BatchGeoStore.getState().MapsMarkersManager.openedInfoWindows.find(
          (openedInfoWindow) => openedInfoWindow.id === id,
        );
      if (openInfoWindow) {
        openInfoWindow.infoWindow.close();
      }

      BatchGeoStore.dispatch([
        {
          type: "ROW_SELECT_DESELECT",
          // Convert all passed IDs to arrays to make it easier to handle
          selectedRows: !(id instanceof Array) ? [id] : id,
        },
      ]);
      //}
    };

    /**
     * Given two elements, this function will select those two elements and all
     * rows inbetween. It uses the displayData method to find the rows inbetween
     * based on finding the first row in display data and it keeps saving the
     * rows to the selection until it finds the last row selected's ID.
     */
    var multiRowElementSelect = function (firstElement, lastElement) {
      var displayData = self.getDisplayData();

      var startIndex, endIndex;

      // Loop through all the displayData rows until we find the start and
      // end indexes so we can figure out what all the rows in between are
      for (var i = 0; i < displayData.length; i++) {
        // If we find the first element save the index
        if ($(firstElement).data("id") == displayData[i].id) {
          startIndex = i;
        }
        // If we found the last element save the index
        if ($(lastElement).data("id") == displayData[i].id) {
          endIndex = i;
        }
        // If both indexes have been found we can exit the loop
        if (startIndex !== undefined && endIndex !== undefined) {
          break;
        }
      }

      // Slice wont work properly if this isn't sorted because you can't
      // slice, for example, slice(9, 1)
      var sortedIndexes = [startIndex, endIndex].sort();

      // Get the row IDs from the slice so we know what IDs were selected
      var selectedRows = displayData
        .slice(sortedIndexes[0], sortedIndexes[1] + 1)
        .map(function (row) {
          return row.id;
        });

      selectRow(selectedRows);
    };

    /**
     * Works similar to deselectRow, but it deselects everything. It fires only
     * one deselect event.
     */
    var deselectAllRows = function () {
      BatchGeoStore.dispatch({ type: "ROW_SELECT_DESELECTALL" });
    };

    /**
     * Returns a cloned array of the selected rows
     * @return {array} A cloned array of the selected rows
     */
    var getSelectedRows = function () {
      return $.extend(true, self.getRowData(getSelectedRowIds()), []);
    };

    /**
     * Mousedown does most of the grunt work for handling selections. It
     * handles single selections and deselections, cmd, click, & shift selects
     * as well as tracks the mousedown for drag selections
     */

    $tbody
      .on("mousedown", "tr", function (event) {
        if (event.button !== 0 || $(event.target).is("a")) return;

        var id = $(this).data("id");
        if (event.shiftKey && lastSelectedElement) {
          // If a user is holding down shift, and there was an element that was
          // previously selected we want to select the range of rows
          multiRowElementSelect(this, lastSelectedElement);

          // IE will try to select stuff unless you use their proprietary HTML
          // attribute that prevents the text from being selected. We turn this
          // off on mouseup
          $("body").attr("unselectable", "on");
        } else if (isCtrlKey(event)) {
          // If the ctrl/cmd key is pressed we just need to toggle on the selected
          // row that was ctrl/cmd+clicked on
          isSelected(id) ? deselectRow(id) : selectAddRow(id);
        } else {
          if (BatchGeoDevices.is("mobile")) {
            // If not a shift or ctrl/cmd click then treat as a normal click. For
            // normal clicks we want to clear all multi selections and go back to
            // single selections. We also want to toggle between selected and
            // deselected if you are clicking on the same row
            if (isSelected(id)) {
              deselectRow(id);
            } else {
              selectAddRow(id);
            }
          } else {
            var shouldToggle =
              isSelected(id) && getSelectedRowIds().length == 1;
            deselectAllRows();
            shouldToggle ? deselectRow(id) : selectRow(id);
          }
        }

        // Keep track of the last selected element for shift clicks
        lastSelectedElement = this;

        // Track of mousedown for things like changing the cursor and drag select
        isMouseDown = true;
      })

      .on("mouseover", "tr", function () {
        if (isMouseDown) {
          // Disable multiRowElementSelect if the device is iOS & mobile size
          if (!(BatchGeoDevices.is("ios") && BatchGeoDevices.is("mobile"))) {
            multiRowElementSelect(this, lastSelectedElement);
          }
          $tbody.addClass("row-select-selecting");
          clearTextSelection();
        }
      })

      .on("mouseup", "tr", function () {
        isMouseDown = false;
        $("body").attr("unselectable", "off");
        $tbody.removeClass("row-select-selecting");
      })
      //handles behavior for double click on desktop
      .on("dblclick", "tr", function () {
        var id = $(this).data("id");
        if (window.batchGeoGoogleMapsMarker.getMarker(id)) {
          // only zoom if the selected row has a valid marker association
          selectRow(id);
          window.batchGeoGoogleMapsMarker.MarkerContent.pointToZoom();
        }
      })
      //handles double click on mobile
      .on("touchstart", "tr", function () {
        if (!tapped) {
          //if tap is not set, set up single tap
          tapped = setTimeout(function () {
            tapped = null;
          }, 300);
        } else {
          //tapped within 300ms of last tap. double tap
          clearTimeout(tapped); //stop single tap callback
          tapped = null;
          var id = $(this).data("id");
          selectRow(id);

          BatchGeoStore.dispatch([
            {
              type: "FEATURE_TOGGLE",
              feature: "shouldRenderTable",
            },
          ]);
        }
      });

    // Public API
    self.RowSelect = {
      select: selectRow,
      selectAll: selectAllRows,
      isRowSelected: isSelected,
      getSelectedRows: getSelectedRows,
      deselect: deselectRow,
      deselectAll: deselectAllRows,
    };
  };

  window.RowSelect = RowSelect;
})(window);
